import type {CampaignSurfaceId} from "analytics/landingVisit";
import type {CampaignCopy as CampaignText} from "i18n/en/campaign";
import {useCampaignSpace} from "./useCampaignSpace";
import {useRef, type RefObject} from "react";
import {useLocale} from "i18n";
import {useResolvedStorefront} from "pricing/useQuotedPricing";
import {formatMoney} from "pricing/PricingStorage";
import {LANDING_OFFER_PLAN, type CampaignOffer} from "./landingOfferPlan";
import type {CampaignFrame, CampaignId} from "./campaignPlayback";
import {campaignPlate, plateQuotesPrice} from "./campaignPlate";
import {CampaignFitStyle, Allocation, AllocationContainer, CampaignReading, CampaignCopy, CampaignCta, CampaignCtaArrow, CampaignLede, CampaignNote, CampaignSlot, CampaignTitle, PlanLabel, PrintColumns} from "./LandingCampaign.styles";

function CopyPlate({id, outgoing = false, dissolving, paused, onOpen, readingRef, monthlyPrice, campaign, surface}: {
	campaign: CampaignText; readingRef?: RefObject<HTMLDivElement | null>; id: CampaignId; outgoing?: boolean; dissolving: boolean; paused: boolean; onOpen: () => void;
	monthlyPrice: string; surface: CampaignSurfaceId;
}) {
	const plate = campaignPlate(campaign, id, monthlyPrice, surface);
	return (
		<CampaignCopy ref={readingRef} $scene={id} $outgoing={outgoing} $dissolving={dissolving} $paused={paused}
			data-edition={id} aria-hidden={outgoing || undefined} inert={outgoing}>
			<PlanLabel>{plate.label}</PlanLabel>
			{/* Only the active message is a heading, including during a dissolve. */}
			<CampaignTitle as={outgoing ? "p" : "h1"}>{plate.title}</CampaignTitle>
			<CampaignLede>{plate.copy}</CampaignLede>
			{id === "newspaper" && <PrintColumns>{campaign.columns.map(column => (
				<div key={column.title}><strong>{column.title}</strong><p>{column.body}</p></div>
			))}</PrintColumns>}
			<CampaignCta as={outgoing ? "span" : "button"} type={outgoing ? undefined : "button"} onClick={outgoing ? undefined : onOpen}>
				{plate.action}<CampaignCtaArrow aria-hidden="true" data-campaign-arrow>
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
						<path d="M5 19 19 5M5 5h14v14"/>
					</svg>
				</CampaignCtaArrow>
			</CampaignCta>
			{plate.note && <CampaignNote>{plate.note}</CampaignNote>}
			<CampaignNote>{plate.caption}</CampaignNote>
		</CampaignCopy>
	);
}

export function LandingCampaign({active, frame, paused, readingRef, onOpen, visibleFraction, assigned, campaign, offerPlan, surface}: {
	campaign: CampaignText; offerPlan: CampaignOffer; assigned: boolean; visibleFraction: number; active: boolean; frame: CampaignFrame; paused: boolean; readingRef: RefObject<HTMLDivElement | null>; onOpen: () => void;
	surface: CampaignSurfaceId;
}) {
	const spaceRef = useRef<HTMLDivElement>(null);
	const {locale} = useLocale();
	// The approved notes quote the same storefront as the plan picker, never the prototype's fixed euros.
	const {pricing} = useResolvedStorefront(active && plateQuotesPrice(campaign, frame.active));
	const monthlyPrice = formatMoney(pricing.currency, pricing[offerPlan === "referral" ? LANDING_OFFER_PLAN : offerPlan].monthly, locale);
	useCampaignSpace(spaceRef, assigned);
	return (
		<CampaignSlot inert={!active} data-testid="landing-campaign" data-slide={frame.active} data-paused={paused} data-visible-fraction={visibleFraction}>
			<CampaignFitStyle/>
			<AllocationContainer aria-hidden="true" inert><Allocation/></AllocationContainer>
			<CampaignReading key={assigned ? "visit" : "ssr"} ref={spaceRef}>
			<CopyPlate campaign={campaign} surface={surface} readingRef={readingRef} key={frame.active} id={frame.active} dissolving={frame.outgoing !== null} paused={paused} onOpen={onOpen} monthlyPrice={monthlyPrice}/>
			{frame.outgoing && <CopyPlate campaign={campaign} surface={surface} key={frame.outgoing} id={frame.outgoing} outgoing dissolving paused={paused} onOpen={onOpen} monthlyPrice={monthlyPrice}/>}
			</CampaignReading>
		</CampaignSlot>
	);
}
